import React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import CardArt from './CardArt';
import { getCategoryIconNode } from '../utils/categoryIcon';
import colors from '../theme/colors';

const CARD_W = 250;
const CARD_H = 350;

const THEME_PRIMARY   = colors.base.themePrimary;
const THEME_SECONDARY = colors.base.themeSecondary;
const PEARL = 'linear-gradient(135deg, #ffffff 0%, #f4f0ff 25%, #fff8fe 50%, #f0f8ff 75%, #ffffff 100%)';

function adjustColor(color, amount) {
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  const r = match ? parseInt(match[1]) : 128;
  const g = match ? parseInt(match[2]) : 128;
  const b = match ? parseInt(match[3]) : 128;
  const clamp = (v) => Math.max(0, Math.min(255, v + amount));
  return `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`;
}

const PARCHMENT = `linear-gradient(135deg, ${colors.base.themeTertiary} 0%, ${adjustColor(colors.base.themeTertiary, -10)} 50%, ${colors.base.themeTertiary} 100%)`;

// Slightly lighter shade of themePrimary — matches CARD_FRAME_COLOR used by the real tree cards
const themePrimaryHsl = colors.utils.rgbToHsl(THEME_PRIMARY);
const CARD_FRAME_COLOR = colors.utils.hslToRgb(themePrimaryHsl.h, themePrimaryHsl.s, themePrimaryHsl.l + 5);

// nodeData: { id, title, shortDescription }
// anchorRect: DOMRect from getBoundingClientRect()
// allNodes: full tree, used to resolve the category artwork of the top-left gem
// onClose: when set (tap-to-preview on mobile), the card is interactive and
// shows a close button in its top-right corner
const NodeCardPreview = ({ nodeData, anchorRect, allNodes = [], onClose }) => {
  if (!nodeData || !anchorRect) return null;

  const { id, title, shortDescription } = nodeData;

  // Top-left gem shows the category artwork, like the real tree card. Without
  // the tree at hand, fall back to the node's own art.
  const gemNode = getCategoryIconNode(allNodes.find(n => n.id === id), allNodes) || { id, title };

  let top  = anchorRect.top - CARD_H - 14;
  let left = anchorRect.left + anchorRect.width / 2 - CARD_W / 2;
  if (top < 8) top = anchorRect.bottom + 14;
  left = Math.max(8, Math.min(left, (window.innerWidth || 1200) - CARD_W - 8));

  return createPortal(
    <motion.div
      style={{
        position: 'fixed',
        top,
        left,
        width: CARD_W,
        height: CARD_H,
        zIndex: 25000,
        pointerEvents: onClose ? 'auto' : 'none',
        /* matches NodeContainer */
        background: CARD_FRAME_COLOR,
        borderRadius: 14,
        border: `4px solid ${THEME_SECONDARY}`,
        boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.4), 0 0 0 2px rgba(0,0,0,0.7), 0 12px 40px rgba(0,0,0,0.8)',
        overflow: 'visible',
      }}
      initial={{ opacity: 0, scale: 0.88, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.88, y: 6 }}
      transition={{ duration: 0.14 }}
    >
      {onClose && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 12,
            width: 26,
            height: 26,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.4)',
            background: 'rgba(0,0,0,0.6)',
            color: 'white',
            fontSize: 12,
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ✕
        </button>
      )}

      {/* Oval portrait zone — matches PortraitZone */}
      <div style={{
        position: 'absolute',
        top: 12,
        left: 12,
        right: 12,
        height: 180,
        borderRadius: '48% 48% 44% 44% / 38% 38% 46% 46%',
        overflow: 'hidden',
        border: `2px solid ${THEME_SECONDARY}`,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.35), inset 0 0 14px rgba(0,0,0,0.12)',
        background: PEARL,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
      }}>
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CardArt id={id} title={title} />
        </div>
        {/* Edge vignette */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at 50% 40%, transparent 50%, rgba(0,0,0,0.22) 100%)',
          pointerEvents: 'none',
        }} />
      </div>

      {/* Name banner — matches NameBanner */}
      <div style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: 181,
        height: 44,
        background: CARD_FRAME_COLOR,
        borderTop: `2px solid ${THEME_SECONDARY}`,
        borderBottom: `2px solid ${THEME_SECONDARY}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 30px',
        zIndex: 5,
      }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: THEME_SECONDARY, fontSize: 8, opacity: 0.8 }}>◆</span>
        <h3 style={{ margin: 0, color: THEME_SECONDARY, fontSize: '0.78em', fontWeight: 700, textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-word' }}>{title}</h3>
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: THEME_SECONDARY, fontSize: 8, opacity: 0.8 }}>◆</span>
      </div>

      {/* Parchment description — matches ParchmentBox */}
      {shortDescription && (
        <div style={{
          position: 'absolute',
          top: 235,
          left: 12,
          right: 12,
          bottom: 27,
          background: PARCHMENT,
          borderRadius: 6,
          border: `2px solid ${THEME_SECONDARY}`,
          boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
          padding: '4px 8px',
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          <p style={{
            margin: 0,
            color: '#1a1a1a',
            fontSize: '0.64rem',
            lineHeight: 1.35,
            fontStyle: 'italic',
            textAlign: 'center',
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>{shortDescription}</p>
        </div>
      )}

      {/* Category gem — matches CategoryGem */}
      <div style={{
        position: 'absolute',
        top: 8,
        left: 8,
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f0f0ff 30%, #fff8ff 60%, #f8ffff 100%)',
        border: '1px solid rgba(255,255,255,0.9)',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.25), 0 2px 6px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
        overflow: 'hidden',
      }}>
        <div style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CardArt id={gemNode.id} title={gemNode.title} />
        </div>
      </div>
    </motion.div>,
    document.body
  );
};

export default NodeCardPreview;
